import { z } from 'zod';
import {
  ResearchSnapshotPropsSchema,
  ResearchSeedAndSidePropsSchema,
} from './widget-schemas';

// ── Snapshot sub-schemas ────────────────────────────────────────────

const SnapshotActionBase = z.object({
  iconSize: z.enum(['L', 'S', 'none']),
  iconName: z.string().optional(),
  title: z.string(),
  description: z.string(),
  // When true, this action is the "featured" card: the home lobby renders it
  // first and applies the prominent (purple-bordered) treatment. Data-driven
  // so the snapshot producer — not a positional heuristic in the UI — decides
  // which action is featured. Optional for backward-compat: snapshots without
  // it parse unchanged and render no featured card.
  featured: z.boolean().optional(),
  // When true, this action is the single PRIMARY call-to-action and must render
  // visually distinct from the suggestion pills (019 FR-028 — e.g. the scoping
  // "Lock the scope & start research" CTA). Distinct from `featured` (the home
  // lobby's prominent card): an action may be primary without being featured.
  // Optional/back-compat: snapshots without it parse unchanged.
  primary: z.boolean().optional(),
});
const SendMessageAction = SnapshotActionBase.extend({
  kind: z.literal('send_message'),
  text: z.string(),
});
const NavigateAction = SnapshotActionBase.extend({
  kind: z.literal('navigate'),
  href: z.string(),
});
const NavigateAndSendAction = SnapshotActionBase.extend({
  kind: z.literal('navigate_and_send'),
  href: z.string(),
  text: z.string(),
});
const WidgetActionVariant = SnapshotActionBase.extend({
  kind: z.literal('widget_action'),
  widget_id: z.string(),
  action: z.string(),
  text: z.string().optional(),
});
export const SnapshotActionSchema = z.discriminatedUnion('kind', [
  SendMessageAction,
  NavigateAction,
  NavigateAndSendAction,
  WidgetActionVariant,
]);
export type SnapshotAction = z.infer<typeof SnapshotActionSchema>;

export const SnapshotRecommendationSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  status: z.enum(['open', 'dismissed']),
  // Phase B-5 (PL-260517): richer fields for the RecommendationCard UI.
  // All optional — existing snapshots without them still parse; the
  // loadRecommendationsList loader filters/sorts based on what's present.
  // Phase C (snapshot-job writer) will populate these fields.
  priority: z.enum(['high', 'medium', 'low']).optional(),
  theme: z.string().optional(),
  summary: z.string().optional(),
  confidence: z.string().optional(),
  signalCount: z.number().int().min(0).optional(),
  interviewCount: z.number().int().min(0).optional(),
});

export const AgendaItemContentSchema = z.object({
  id: z.string().uuid(),
  content: z.string(),
  status: z.enum(['to_discuss', 'discussed']),
});

export const AgendaBlockSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(['meeting', 'general']),
  title: z.string(),
  items: z.array(AgendaItemContentSchema),
});

export const SnapshotGreetingSchema = z.object({
  title: z.string().min(1).max(120),
  subtitle: z.string().min(1).max(280),
});

// ── ProjectSnapshotSchema (projects.snapshot JSONB) ─────────────────

export const ProjectSnapshotSchema = z.object({
  actions: z.array(SnapshotActionSchema).optional(),
  confidence: z.object({
    score: z.number().int().min(0).max(100),
    trend: z.enum(['up', 'down', 'neutral']),
  }).optional(),
  greeting: SnapshotGreetingSchema.optional(),
  interviews: z.object({
    count: z.number().int(),
    completed: z.number().int(),
    scheduled: z.number().int(),
  }).optional(),
  research: z.object({
    current_phase: z.string(),
    phase_index: z.number().int(),
  }).optional(),
  recommendations: z.array(SnapshotRecommendationSchema).optional(),
  chat: z.object({
    agenda: z.array(AgendaBlockSchema).optional(),
  }).optional(),
  /**
   * PL-260517 design-handoff data-wiring namespace.
   *
   * New page-shaped fields the redesigned Research / Interviews surfaces
   * read live here instead of collapsing into the legacy keys above. This
   * avoids reconciling the live `ic_project_snapshot_v0` shape (`evidence`,
   * `counts`, `confidence`) with the legacy `ProjectSnapshotSchema` keys
   * the home page reads (`greeting`, `actions`).
   *
   * All fields optional — snapshot must keep parsing pre-population.
   */
  ui: z.object({
    research: z.object({
      snapshot: ResearchSnapshotPropsSchema.optional(),
      seedAndSide: ResearchSeedAndSidePropsSchema.optional(),
      // Top-bets list NOT mirrored here. UI reads bets directly from
      // /bets via useBetsList; see PL-260517 §4.1.
    }).optional(),
    interviewsRollup: z.object({
      upcomingCount: z.number().int().min(0).optional(),
      liveCount: z.number().int().min(0).optional(),
      completedCount: z.number().int().min(0).optional(),
      nextInterview: z.object({
        id: z.string().uuid(),
        participantName: z.string(),
        startsIn: z.string(),
      }).optional(),
    }).optional(),
    // ANA-125: plan artefact download URL written by the n8n planning pipeline.
    // When present, state-8 home shows a Download button; absent → graceful hide.
    planArtefactUrl: z.string().url().optional(),
  }).optional(),
});

// ── ICP schemas ─────────────────────────────────────────────────────

export const ICPCriterionSchema = z.object({
  label: z.string(),
  value: z.string(),
  description: z.string().optional(),
});

export const ProjectMetadataICPSchema = z.object({
  defined_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  criteria: z.array(ICPCriterionSchema),
});

// ── Participant score schemas ───────────────────────────────────────

export const ParticipantFitLevelEnum = z.enum(['high', 'medium', 'low']);

export const ParticipantScoreSchema = z.object({
  id: z.string().uuid(),
  participant_id: z.string().uuid(),
  project_id: z.string().uuid(),
  phase_id: z.string().uuid().nullable(),
  fit_level: ParticipantFitLevelEnum,
  fit_score: z.number().int().min(0).max(100),
  rationale: z.string().optional(),
  enrichment_hash: z.string().optional(),
  scored_by: z.string().optional(),
  scored_at: z.string().datetime({ offset: true }),
});

// ── Profile settings ────────────────────────────────────────────────

/**
 * Chat layout preference mode.
 *
 * - `peek`: slim hint pill at the bottom (no input bar visible).
 * - `detailed`: chat input bar + (optional) last Anaka response card visible;
 *   dashboard widgets still rendered.
 * - `focused`: full-page thread with widgets hidden, transparent thread
 *   surface, bubbles sit directly on the page.
 *
 * Any saved value not in this enum should be reset to the route default —
 * there is no migration shim for the legacy 2-mode (`focus` / `detailed`)
 * preference. Reading code is responsible for falling back when it sees an
 * unexpected value.
 */
const ChatLayoutModeEnum = z.enum(['peek', 'detailed', 'focused']);

const ChatLayoutPrefsSchema = z
  .object({
    overview: ChatLayoutModeEnum.nullable().optional(),
    other: ChatLayoutModeEnum.nullable().optional(),
  })
  .strict();

/**
 * Extended profile fields edited on the /settings page (spec 033).
 * Stored namespaced under `settings.profile.*` (the all-new block); the existing
 * `theme` / `inputMode` preference keys stay flat — see research R2. Email + role
 * are display-only (not here); "Full name" is a column (`profiles.full_name`), not
 * a settings field. The Anaka-voice picker is deferred (IDE-47).
 */
const ProfileFieldsSchema = z
  .object({
    preferredName: z.string().max(60).optional(),
    jobTitle: z.string().max(80).optional(),
    location: z.string().max(80).optional(),
    bio: z.string().max(500).optional(),
    language: z.string().max(60).optional(),
    timezone: z.string().max(60).optional(),
  })
  .strict();

export const ProfileSettingsSchema = z
  .object({
    inputMode: z.enum(['voice', 'text']).optional(),
    theme: z.enum(['auto', 'light', 'dark']).optional(),
    chatLayoutPrefs: ChatLayoutPrefsSchema.optional(),
    // spec 033 — new preference flags (flat) + namespaced profile block.
    // `reducedMotion` (ANA-311) was removed as a no-op — nothing ever read
    // the persisted flag. `interfaceSounds` is unrelated and stays (out of
    // scope for ANA-311; its UI control was already removed separately).
    interfaceSounds: z.boolean().optional(),
    profile: ProfileFieldsSchema.optional(),
  })
  .strict();

/**
 * Column-backed identity fields editable on /settings (spec 033). "Full name"
 * writes `profiles.full_name` and re-derives `first_name`; email + role are
 * display-only. Settings-jsonb fields go through ProfileSettingsSchema instead.
 */
export const ProfileIdentityPatchSchema = z
  .object({
    fullName: z.string().trim().min(1).max(80),
  })
  .strict();

// ── Inferred TypeScript types ───────────────────────────────────────

export type SnapshotRecommendation = z.infer<typeof SnapshotRecommendationSchema>;
export type AgendaItemContent = z.infer<typeof AgendaItemContentSchema>;
export type AgendaBlock = z.infer<typeof AgendaBlockSchema>;
export type SnapshotGreeting = z.infer<typeof SnapshotGreetingSchema>;
export type ProjectSnapshot = z.infer<typeof ProjectSnapshotSchema>;
export type ICPCriterion = z.infer<typeof ICPCriterionSchema>;
export type ProjectMetadataICP = z.infer<typeof ProjectMetadataICPSchema>;
export type ParticipantFitLevel = z.infer<typeof ParticipantFitLevelEnum>;
export type ParticipantScore = z.infer<typeof ParticipantScoreSchema>;
export type ProfileSettings = z.infer<typeof ProfileSettingsSchema>;
export type ProfileFields = z.infer<typeof ProfileFieldsSchema>;
export type ProfileIdentityPatch = z.infer<typeof ProfileIdentityPatchSchema>;
export type ChatLayoutPrefs = z.infer<typeof ChatLayoutPrefsSchema>;
export type ChatLayoutMode = z.infer<typeof ChatLayoutModeEnum>;
